/**
 * MARKETING COMPASS — QUESTION BANK
 * ─────────────────────────────────
 * A faithful transcription of the "Question Bank" sheet of
 * docs/author-marketing-marketplace-v2.xlsx: 16 questions, options verbatim
 * (including every "I don't know" variant), gate/score types and weights.
 *
 * Config, not JSX (spec §3.3) — questions can be edited here without touching
 * a component. `marketingQuizConfig.test.js` asserts the counts, types and
 * weights against the sheet so drift is caught rather than discovered.
 *
 * ⚠️ This file is the CONTRACT with the sheet. If the sheet changes, change
 * this file and the test together, in the same commit.
 *
 * ── Terms ──
 * `gate`  — eliminates products whose fields conflict with the answer.
 * `score` — ranks the survivors; never eliminates.
 * `dontKnow` — the option value meaning "I don't know". Per spec §3.3.1 a
 *   don't-know answer on a GATE must NOT eliminate anything; the engine records
 *   an assumption instead. Every gate question that offers one names it here so
 *   the engine never has to string-match the label.
 * `filters` — the Products column this question acts on, straight from the
 *   sheet's "Filters on" column. Kept for the Phase 5 matching engine.
 */

/**
 * Genres offered by Q1. Taken from the distinct `Genre fit` tokens in the
 * product catalogue, because these are exactly the values gating compares
 * against — a genre the catalogue never mentions could not filter anything.
 * "All"/"All incl. niche" are catalogue-side wildcards, not author choices, so
 * they are not offered here.
 */
export const GENRE_OPTIONS = [
  'Romance', 'Romantasy', 'Fantasy', 'SF', 'Mystery', 'Thriller', 'Cosy', 'YA',
];

/** Author-facing stage values used by Q2's gate on the `Stage fit` column. */
export const STAGE_VALUES = ['Pre-launch', 'Launch', 'Relaunch', 'Backlist', 'List-building'];

export const MARKETING_QUIZ = [
  {
    id: 'q1',
    question: 'What genre do you write?',
    optionsSource: 'Genre list / Multiple / I don\'t know how to categorise it',
    // MULTI-SELECT (Julie, 2026-07-30). The sheet's single-select "Multiple"
    // asked authors to flatten a real answer into a shrug; picking the actual
    // genres gates far better — the engine ORs across every selection. The
    // `multiple` option is retained so existing responses stay valid, but it is
    // no longer how you say "more than one".
    multiSelect: true,
    options: [
      ...GENRE_OPTIONS.map((g) => ({ value: g.toLowerCase(), label: g })),
      { value: 'multiple', label: 'Multiple' },
      // Free text. The catalogue taxonomy cannot match it, so the engine treats
      // it as gate-neutral and records an assumption rather than eliminating
      // everything. The text is captured so the taxonomy can grow from real
      // answers instead of guesses.
      { value: 'other', label: 'Other', freeText: true },
      { value: 'dont_know', label: "I don't know how to categorise it", exclusive: true },
    ],
    filters: 'Genre fit',
    type: 'gate',
    weight: 5,
    dontKnow: 'dont_know',
    prefill: 'yes',
    prefillSource: "member's books",
  },
  {
    id: 'q2',
    question: 'How many books do you have out?',
    optionsSource: '0 (pre-launch) / 1 / 2-4 / 5+',
    options: [
      { value: '0', label: '0 (pre-launch)' },
      { value: '1', label: '1' },
      { value: '2-4', label: '2-4' },
      { value: '5+', label: '5+' },
    ],
    filters: 'Stage fit',
    type: 'gate',
    weight: 5,
    dontKnow: null,          // the sheet offers no don't-know here
    prefill: 'yes',
    prefillSource: 'catalogue',
  },
  {
    id: 'q3',
    question: "What's your main goal right now?",
    optionsSource: "Launch / Sell backlist / Grow email list / Get reviews / Sell direct / I don't know",
    options: [
      { value: 'launch', label: 'Launch' },
      { value: 'sell_backlist', label: 'Sell backlist' },
      { value: 'grow_email_list', label: 'Grow email list' },
      { value: 'get_reviews', label: 'Get reviews' },
      { value: 'sell_direct', label: 'Sell direct' },
      { value: 'dont_know', label: "I don't know" },
    ],
    filters: 'Stage fit + category routing',
    type: 'gate',
    weight: 5,
    dontKnow: 'dont_know',
    prefill: 'partly',
    prefillSource: 'release dates',
  },
  {
    id: 'q4',
    question: "What's your monthly marketing budget?",
    optionsSource: "£0 / <£50 / £50-200 / £200+ / I don't know",
    options: [
      { value: '0', label: '£0' },
      { value: 'under_50', label: '<£50' },
      { value: '50_200', label: '£50-200' },
      { value: '200_plus', label: '£200+' },
      { value: 'dont_know', label: "I don't know" },
    ],
    filters: 'Price low/high',
    type: 'score',
    weight: 4,
    dontKnow: 'dont_know',
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q5',
    question: 'Hours per week for marketing?',
    optionsSource: '<2 / 2-5 / 5-10 / 10+',
    options: [
      { value: 'under_2', label: '<2' },
      { value: '2_5', label: '2-5' },
      { value: '5_10', label: '5-10' },
      { value: '10_plus', label: '10+' },
    ],
    filters: 'Skill level + DIY vs done-for-you',
    type: 'score',
    weight: 3,
    dontKnow: null,
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q6',
    question: 'Do you have an email list?',
    optionsSource: "No / <100 / 100-1,000 / 1,000+ / I don't know what counts",
    options: [
      { value: 'no', label: 'No' },
      { value: 'under_100', label: '<100' },
      { value: '100_1000', label: '100-1,000' },
      { value: '1000_plus', label: '1,000+' },
      { value: 'dont_know', label: "I don't know what counts" },
    ],
    filters: 'Cats 06 & 07 routing',
    type: 'score',
    weight: 4,
    dontKnow: 'dont_know',
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q7',
    question: 'Book price for this push?',
    optionsSource: 'Free / £0.99-2.99 / Full price / Not sure yet',
    options: [
      { value: 'free', label: 'Free' },
      { value: '0_99_2_99', label: '£0.99-2.99' },
      { value: 'full_price', label: 'Full price' },
      { value: 'not_sure', label: 'Not sure yet' },
    ],
    filters: 'Deal-site requirements',
    type: 'gate',
    weight: 5,
    dontKnow: 'not_sure',      // "Not sure yet" IS this question's don't-know
    prefill: 'partly',
    prefillSource: 'platform pricing',
  },
  {
    id: 'q8',
    question: 'How many reviews does the book have?',
    optionsSource: "<10 / 10-50 / 50+ / I don't know",
    options: [
      { value: 'under_10', label: '<10' },
      { value: '10_50', label: '10-50' },
      { value: '50_plus', label: '50+' },
      { value: 'dont_know', label: "I don't know" },
    ],
    filters: 'Requirements (Robin Reads etc.)',
    type: 'gate',
    weight: 4,
    dontKnow: 'dont_know',
    // Sheet says "Partly", but the only review data on the platform is ARC
    // reviews, and this gate is about RETAILER review counts. See prefill.js.
    prefill: 'partly',
    prefillSource: null,
  },
  {
    id: 'q9',
    question: 'Amazon-exclusive or wide?',
    optionsSource: "KDP Select / Wide / I don't know what this means",
    options: [
      { value: 'kdp_select', label: 'KDP Select' },
      { value: 'wide', label: 'Wide' },
      { value: 'dont_know', label: "I don't know what this means" },
    ],
    filters: 'KU / Wide relevance',
    type: 'gate',
    weight: 5,
    dontKnow: 'dont_know',
    prefill: 'yes',
    prefillSource: 'distribution data',
  },
  {
    id: 'q10',
    question: 'Do you have an audiobook?',
    optionsSource: 'Yes / No / In production',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'in_production', label: 'In production' },
    ],
    filters: 'Cat 09 audio routing',
    type: 'gate',
    weight: 3,
    dontKnow: null,
    prefill: 'yes',
    prefillSource: 'Narratoria audio data',
  },
  {
    id: 'q11',
    question: 'What marketing assets do you already have?',
    optionsSource: 'Multi-select: pro cover / brand kit / author website / social accounts / video content / none / not sure',
    multiSelect: true,
    options: [
      { value: 'pro_cover', label: 'Pro cover' },
      { value: 'brand_kit', label: 'Brand kit' },
      { value: 'author_website', label: 'Author website' },
      { value: 'social_accounts', label: 'Social accounts' },
      { value: 'video_content', label: 'Video content' },
      { value: 'none', label: 'None', exclusive: true },
      { value: 'not_sure', label: 'Not sure', exclusive: true },
    ],
    filters: 'Cats 01, 13, 14 routing',
    type: 'gate',
    weight: 4,
    dontKnow: 'not_sure',
    prefill: 'partly',
    prefillSource: 'cover on platform',
  },
  {
    id: 'q12',
    question: 'How confident are you making graphics?',
    optionsSource: 'Happy in Canva / Can follow templates / Please no',
    options: [
      { value: 'happy_in_canva', label: 'Happy in Canva' },
      { value: 'can_follow_templates', label: 'Can follow templates' },
      { value: 'please_no', label: 'Please no' },
    ],
    filters: 'Skill level; cat 01 vs 15',
    type: 'score',
    weight: 3,
    dontKnow: null,
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q13',
    question: 'How do you feel about creating content?',
    optionsSource: 'Happy on camera / Voice/text only / Faceless graphics only / Rather not at all',
    options: [
      { value: 'happy_on_camera', label: 'Happy on camera' },
      { value: 'voice_text_only', label: 'Voice/text only' },
      { value: 'faceless_graphics_only', label: 'Faceless graphics only' },
      { value: 'rather_not', label: 'Rather not at all' },
    ],
    filters: 'Cats 02, 04 (TikTok), 15 routing',
    type: 'gate',
    weight: 4,
    dontKnow: null,
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q14',
    question: 'Heat/content level of your books?',
    optionsSource: 'Sweet-clean / Mid / High heat / Dark content warnings',
    options: [
      { value: 'sweet_clean', label: 'Sweet-clean' },
      { value: 'mid', label: 'Mid' },
      { value: 'high_heat', label: 'High heat' },
      { value: 'dark_content_warnings', label: 'Dark content warnings' },
    ],
    filters: 'Fussy Librarian, My Book Cave, ad policies',
    type: 'gate',
    weight: 3,
    dontKnow: null,
    prefill: 'partly',
    prefillSource: 'book metadata',
  },
  {
    id: 'q15',
    question: 'Would your readers buy special editions or subscriptions?',
    optionsSource: "Definitely / Maybe / No / I don't know",
    options: [
      { value: 'definitely', label: 'Definitely' },
      { value: 'maybe', label: 'Maybe' },
      { value: 'no', label: 'No' },
      { value: 'dont_know', label: "I don't know" },
    ],
    filters: 'Cats 11, 12 routing',
    type: 'score',
    weight: 2,
    dontKnow: 'dont_know',
    prefill: 'no',
    prefillSource: null,
  },
  {
    id: 'q16',
    question: 'Do you know what content to post?',
    optionsSource: "Yes, just need tools / I need ideas / Don't know where to start",
    options: [
      { value: 'yes_need_tools', label: 'Yes, just need tools' },
      { value: 'need_ideas', label: 'I need ideas' },
      { value: 'dont_know_where_to_start', label: "Don't know where to start" },
    ],
    filters: 'Cat 02 routing',
    type: 'score',
    weight: 3,
    dontKnow: 'dont_know_where_to_start',
    prefill: 'no',
    prefillSource: null,
  },
];

/* ── Derived helpers ─────────────────────────────────────── */

/** Option values that carry a free-text field, by question. */
export const FREE_TEXT_OPTION = { q1: 'other' };

/**
 * Normalise a stored answer to the shape the engine expects.
 *
 * q1 was single-select before 2026-07-30, so historical responses hold a bare
 * string. They migrate as ONE-ITEM ARRAYS rather than being rewritten in
 * Firestore — a read-time migration cannot half-fail the way a backfill can.
 */
export function normaliseAnswer(questionId, answer) {
  const q = getQuestion(questionId);
  if (!q) return answer;
  if (q.multiSelect && typeof answer === 'string') return [answer];
  return answer;
}

export const QUESTION_IDS = MARKETING_QUIZ.map((q) => q.id);
export const QUESTION_COUNT = MARKETING_QUIZ.length;

export const getQuestion = (id) => MARKETING_QUIZ.find((q) => q.id === id) || null;
export const gateQuestions = () => MARKETING_QUIZ.filter((q) => q.type === 'gate');
export const scoreQuestions = () => MARKETING_QUIZ.filter((q) => q.type === 'score');

/** Is this answer the question's "I don't know"? Used by gating and assumptions. */
export function isDontKnow(questionId, answer) {
  const q = getQuestion(questionId);
  if (!q || !q.dontKnow) return false;
  return Array.isArray(answer) ? answer.includes(q.dontKnow) : answer === q.dontKnow;
}

/** Valid option values for a question — used to reject junk before persisting. */
export function validOptionValues(questionId) {
  const q = getQuestion(questionId);
  return q ? q.options.map((o) => o.value) : [];
}

/**
 * Is `answer` a structurally valid response to `questionId`?
 * Multi-select expects an array; single-select expects one known value.
 */
export function isValidAnswer(questionId, answer) {
  const q = getQuestion(questionId);
  if (!q) return false;
  const valid = validOptionValues(questionId);
  if (q.multiSelect) {
    return Array.isArray(answer) && answer.length > 0 && answer.every((a) => valid.includes(a));
  }
  return typeof answer === 'string' && valid.includes(answer);
}
